import { create } from "zustand";
import { addCartItem, dedupeCart } from "./cart";

export type GameTab = "bingo" | "wordsearch" | "crossword" | "partygames" | "scavenger";

export interface CartItem {
  id: string;
  gameType: string;
  title: string;
  price: string;
  priceAmount: number;
  sessionId: string;
  gridSize?: number;
  cardCount?: number;
}

export type FontStyle = "classic" | "modern" | "playful" | "elegant";

export interface PersonalizationOptions {
  headerColor: string;
  accentColor: string;
  fontStyle: FontStyle;
  backgroundImageBase64: string;
  backgroundImageFileName: string;
  backgroundImagePreview: string;
}

// Neo-brutal brand defaults — sky-tertiary header + sunny-yellow accent.
// Replaces the pre-redesign violet/pink defaults (`#6C63FF` / `#FF6B6B`)
// flagged by iter-2 critic finding M-2 (color-picker swatches landed
// off-brand on first paint).
const DEFAULT_PERSONALIZATION: PersonalizationOptions = {
  headerColor: "#00BFFF",
  accentColor: "#FFD700",
  fontStyle: "classic",
  backgroundImageBase64: "",
  backgroundImageFileName: "",
  backgroundImagePreview: "",
};

interface BingoForm {
  items: string;
  gridSize: 3 | 4 | 5;
  cardCount: number;
  cardsPerPage: 1 | 2 | 4;
  title: string;
  header: string;
  footer: string;
  logoBase64: string;
  logoFileName: string;
  logoPreview: string;
  includeFreeSpace: boolean;
  selectedCategory: string;
}

interface WordSearchForm {
  words: string;
  gridSize: 10 | 15 | 20;
  title: string;
  header: string;
  footer: string;
  selectedCategory: string;
}

interface CrosswordForm {
  words: string;
  title: string;
  header: string;
  footer: string;
  selectedCategory: string;
  difficulty: "easy" | "medium" | "hard";
}

interface TriviaForm {
  selectedCategories: string[];
  difficulty: "" | "easy" | "medium" | "hard";
  questionCount: number;
  multipleChoice: boolean;
  title: string;
  header: string;
  footer: string;
  teamCount: number;
}

interface BabyShowerForm {
  momName: string;
  babyName: string;
  babyGender: string;
  header: string;
  footer: string;
}

interface BridalShowerForm {
  brideName: string;
  groomName: string;
  weddingDate: string;
  header: string;
  footer: string;
}

export interface CardGameFormState {
  selectedCategories: string[];
  count: number;
  theme: string;
  cardsPerPage: number;
  header: string;
  footer: string;
}

interface PartyGameForm {
  guestOfHonor: string;
  partnerName: string;
  eventType: string;
  title: string;
  header: string;
  footer: string;
}

interface ScavengerForm {
  items: string;
  title: string;
  theme: string;
  cardCount: number;
  header: string;
  footer: string;
}

export interface BrandingState {
  imageBase64: string;      // base64-encoded image data (without data: prefix)
  imageDataUrl: string;     // full data URL for display
  imageMimeType: string;    // e.g. "image/png"
  imageFileName: string;
}

const DEFAULT_BRANDING: BrandingState = {
  imageBase64: "",
  imageDataUrl: "",
  imageMimeType: "",
  imageFileName: "",
};

interface AppState {
  // Active tab
  activeTab: GameTab;
  setActiveTab: (tab: GameTab) => void;

  // Global branding image (applied to all PDFs)
  branding: BrandingState;
  setBranding: (b: BrandingState) => void;
  clearBranding: () => void;

  // Cookie consent
  cookieConsent: boolean | null; // null = not yet answered
  setCookieConsent: (value: boolean) => void;

  // Cart
  cart: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: string) => void;
  clearCart: () => void;

  // Personalization (shared across all game types)
  personalization: PersonalizationOptions;
  updatePersonalization: (updates: Partial<PersonalizationOptions>) => void;

  // Form data per tab
  bingoForm: BingoForm;
  updateBingoForm: (updates: Partial<BingoForm>) => void;

  wordSearchForm: WordSearchForm;
  updateWordSearchForm: (updates: Partial<WordSearchForm>) => void;

  crosswordForm: CrosswordForm;
  updateCrosswordForm: (updates: Partial<CrosswordForm>) => void;

  partyGameForm: PartyGameForm;
  updatePartyGameForm: (updates: Partial<PartyGameForm>) => void;

  scavengerForm: ScavengerForm;
  updateScavengerForm: (updates: Partial<ScavengerForm>) => void;

  triviaForm: TriviaForm;
  updateTriviaForm: (updates: Partial<TriviaForm>) => void;

  babyShowerForm: BabyShowerForm;
  updateBabyShowerForm: (updates: Partial<BabyShowerForm>) => void;

  bridalShowerForm: BridalShowerForm;
  updateBridalShowerForm: (updates: Partial<BridalShowerForm>) => void;

  cardGameForms: Record<string, CardGameFormState>;
  updateCardGameForm: (apiPath: string, updates: Partial<CardGameFormState>) => void;
}

// Load persisted values from cookies/localStorage
function loadPersisted<T>(key: string, defaultValue: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored) return JSON.parse(stored);
  } catch {}
  return defaultValue;
}

function persist(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

const useAppStore = create<AppState>((set) => ({
  activeTab: loadPersisted<GameTab>("pph_tab", "bingo"),
  setActiveTab: (tab) => {
    set({ activeTab: tab });
    persist("pph_tab", tab);
  },

  branding: loadPersisted("pph_branding", DEFAULT_BRANDING),
  setBranding: (b) => {
    set({ branding: b });
    persist("pph_branding", b);
  },
  clearBranding: () => {
    set({ branding: DEFAULT_BRANDING });
    persist("pph_branding", DEFAULT_BRANDING);
  },

  cookieConsent: loadPersisted<boolean | null>("pph_cookies", null),
  setCookieConsent: (value) => {
    set({ cookieConsent: value });
    persist("pph_cookies", value);
  },

  cart: dedupeCart(loadPersisted<CartItem[]>("pph_cart", [])),
  addToCart: (item) =>
    set((state) => {
      // Dedupe by sessionId (see ./cart.ts) — adding the same preview twice is a no-op.
      const newCart = addCartItem(state.cart, item);
      if (newCart === state.cart) return {};
      persist("pph_cart", newCart);
      return { cart: newCart };
    }),
  removeFromCart: (id) =>
    set((state) => {
      const newCart = state.cart.filter((item) => item.id !== id);
      persist("pph_cart", newCart);
      return { cart: newCart };
    }),
  clearCart: () => {
    persist("pph_cart", []);
    set({ cart: [] });
  },

  personalization: loadPersisted("pph_personalization", DEFAULT_PERSONALIZATION),
  updatePersonalization: (updates) =>
    set((state) => {
      const newOpts = { ...state.personalization, ...updates };
      persist("pph_personalization", newOpts);
      return { personalization: newOpts };
    }),

  bingoForm: loadPersisted("pph_bingo", {
    items: "",
    gridSize: 5 as 3 | 4 | 5,
    cardCount: 40,
    cardsPerPage: 1 as 1 | 2 | 4,
    title: "",
    header: "",
    footer: "",
    logoBase64: "",
    logoFileName: "",
    logoPreview: "",
    includeFreeSpace: true,
    selectedCategory: "",
  }),
  updateBingoForm: (updates) =>
    set((state) => {
      const newForm = { ...state.bingoForm, ...updates };
      persist("pph_bingo", newForm);
      return { bingoForm: newForm };
    }),

  wordSearchForm: loadPersisted("pph_ws", {
    words: "",
    gridSize: 15 as 10 | 15 | 20,
    title: "",
    header: "",
    footer: "",
    selectedCategory: "",
  }),
  updateWordSearchForm: (updates) =>
    set((state) => {
      const newForm = { ...state.wordSearchForm, ...updates };
      persist("pph_ws", newForm);
      return { wordSearchForm: newForm };
    }),

  crosswordForm: loadPersisted("pph_cw", {
    words: "",
    title: "",
    header: "",
    footer: "",
    selectedCategory: "",
    difficulty: "medium" as "easy" | "medium" | "hard",
  }),
  updateCrosswordForm: (updates) =>
    set((state) => {
      const newForm = { ...state.crosswordForm, ...updates };
      persist("pph_cw", newForm);
      return { crosswordForm: newForm };
    }),

  partyGameForm: loadPersisted("pph_pg", {
    guestOfHonor: "",
    partnerName: "",
    eventType: "baby-shower",
    title: "",
    header: "",
    footer: "",
  }),
  updatePartyGameForm: (updates) =>
    set((state) => {
      const newForm = { ...state.partyGameForm, ...updates };
      persist("pph_pg", newForm);
      return { partyGameForm: newForm };
    }),

  scavengerForm: loadPersisted("pph_sh", {
    items: "",
    title: "",
    theme: "",
    cardCount: 10,
    header: "",
    footer: "",
  }),
  updateScavengerForm: (updates) =>
    set((state) => {
      const newForm = { ...state.scavengerForm, ...updates };
      persist("pph_sh", newForm);
      return { scavengerForm: newForm };
    }),

  triviaForm: loadPersisted("pph_trivia", {
    selectedCategories: [],
    difficulty: "" as "" | "easy" | "medium" | "hard",
    questionCount: 25,
    multipleChoice: true,
    title: "Trivia Night",
    header: "",
    footer: "",
    teamCount: 4,
  }),
  updateTriviaForm: (updates) =>
    set((state) => {
      const newForm = { ...state.triviaForm, ...updates };
      persist("pph_trivia", newForm);
      return { triviaForm: newForm };
    }),

  babyShowerForm: loadPersisted("pph_baby", { momName: "", babyName: "", babyGender: "unknown", header: "", footer: "" }),
  updateBabyShowerForm: (updates) =>
    set((state) => {
      const newForm = { ...state.babyShowerForm, ...updates };
      persist("pph_baby", newForm);
      return { babyShowerForm: newForm };
    }),

  bridalShowerForm: loadPersisted("pph_bridal", { brideName: "", groomName: "", weddingDate: "", header: "", footer: "" }),
  updateBridalShowerForm: (updates) =>
    set((state) => {
      const newForm = { ...state.bridalShowerForm, ...updates };
      persist("pph_bridal", newForm);
      return { bridalShowerForm: newForm };
    }),

  cardGameForms: loadPersisted("pph_cardgame", {}),
  updateCardGameForm: (apiPath, updates) =>
    set((state) => {
      const current = state.cardGameForms[apiPath] || { selectedCategories: [], count: 40, theme: "party", cardsPerPage: 9, header: "", footer: "" };
      const newForm = { ...current, ...updates };
      const newForms = { ...state.cardGameForms, [apiPath]: newForm };
      persist("pph_cardgame", newForms);
      return { cardGameForms: newForms };
    }),
}));

export default useAppStore;
